import { Button, Checkbox, IPublicRestaurant, Label, StringUtils } from '@localina/core';
import { CompanyIcon, CookieIcon, EmailIcon, InfoIcon, MobileIcon, PersonIcon, TextBlockIcon } from '@localina/icons';
import { Typography } from '@mui/material';
import { formatNumber } from 'libphonenumber-js';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { ISummaryRow, ReservationSummaryCard, SummaryCard } from '../../../components';
import { useApp } from '../../../contexts';
import { IApplicationStore, IReservationDateTime, IReservationPerson } from '../../../interfaces';
import { reservationActions } from '../../../store';

interface IActionProps {
    saveReservationPerson: typeof reservationActions.saveReservationPerson;
    saveReservation: typeof reservationActions.saveReservationRequested;
}

interface IStoreProps {
    reservation: IReservationDateTime;
    person: IReservationPerson;
    isLoading: boolean;
    hasError: boolean;
    error: any;
}

interface IProps extends IActionProps, IStoreProps {
    restaurant: IPublicRestaurant;
    onNext: () => void;
    onBack: () => void;
}

const Summary: React.FC<IProps> = (props) => {
    const { t } = useTranslation();
    const { restaurantId } = useApp();

    const [person, setPerson] = React.useState(props.person);
    const [saveRequested, setSaveRequested] = React.useState(false);
    const errorRef = React.useRef<HTMLDivElement>(null);
    const handleChanges = (value: boolean) => {
        setPerson({
            ...person,
            subscribeToNewsletter: value,
        });
    };

    const handleBack = () => {
        props.onBack();
    };

    const handleSend = () => {
        setSaveRequested(true);
        props.saveReservationPerson(person);
        props.saveReservation(restaurantId, props.reservation, person);
    };

    React.useEffect(() => {
        if (saveRequested && !props.isLoading && !props.hasError) {
            props.onNext();
        }
    }, [props.isLoading]);

    React.useEffect(() => {
        if (props.hasError) {
            errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [props.hasError, props.error]);

    const personRows: ISummaryRow[] = [
        {
            key: 'name',
            icon: <PersonIcon />,
            label: t('reservation.fields.name'),
            value: StringUtils.combineStrings([props.person.firstName, props.person.lastName]),
        },
    ];
    if (props.person.company) {
        personRows.push({
            key: 'company',
            icon: <CompanyIcon />,
            label: t('common.fields.company'),
            value: props.person.company,
        });
    }
    if (props.person.phoneNumber) {
        personRows.push({
            key: 'phone',
            icon: <MobileIcon />,
            label: t('common.fields.phoneNumber'),
            value: formatNumber(props.person.phoneNumber, 'INTERNATIONAL') || props.person.phoneNumber,
        });
    }
    personRows.push(
        { key: 'email', icon: <EmailIcon />, label: t('common.fields.email'), value: props.person.email },
        {
            key: 'allergies',
            icon: <CookieIcon />,
            label: t('reservation.person.allergies'),
            value: props.person.allergies || t('reservation.summary.noAllergies'),
        },
    );
    if (props.person.comment) {
        personRows.push({
            key: 'notes',
            icon: <TextBlockIcon />,
            label: t('common.fields.comment'),
            value: props.person.comment,
        });
    }

    return (
        <>
            <div className="widget-heading">
                <Label type="title" variant="h5" value={t('reservation.summary.title')} />
                <p className="widget-lead">{t('reservation.summary.lead')}</p>
            </div>
            <ReservationSummaryCard />
            <SummaryCard className="person" rows={personRows} />
            <div className="newsletter">
                <Checkbox
                    checked={person.subscribeToNewsletter}
                    label={t('reservation.summary.fields.newsletter')}
                    onChange={handleChanges}
                />
            </div>
            <div className="terms-of-use">
                <Typography>
                    <Trans i18nKey="reservation.summary.fields.termsOfUse">
                        Here we have the <a>terms of use</a> and here we have <a>Data protection</a>. Read it.
                    </Trans>
                </Typography>
            </div>
            <div className="grow" />
            {props.hasError && (
                <div className="summary-error" role="alert" ref={errorRef}>
                    <InfoIcon />
                    <Label
                        type="text"
                        error
                        value={
                            <Trans
                                shouldUnescape
                                values={{ phone: props.restaurant.phoneNumber }}
                                i18nKey={props.error.errorKey ?? 'reservation.summary.error'}
                            >
                                Oops, something went wrong. We are sorry, this should not happen. Please try it again or
                                call the restaurant directly
                                <a href={`tel:${props.restaurant.phoneNumber}`}>{props.restaurant.phoneNumber}</a>. We
                                apologize for your inconvenience.
                            </Trans>
                        }
                    />
                </div>
            )}
            <div className="buttons">
                <Button label={t('common.buttons.back')} secondary={true} onClick={handleBack} />
                <Button label={t('common.buttons.send')} onClick={handleSend} />
            </div>
        </>
    );
};

const actions: IActionProps = {
    saveReservationPerson: reservationActions.saveReservationPerson,
    saveReservation: reservationActions.saveReservationRequested,
};

const mapStateToProps = (state: IApplicationStore): IStoreProps => ({
    reservation: state.reservation.datetime,
    person: state.reservation.person,
    isLoading: state.reservation.isLoading,
    hasError: state.reservation.hasError,
    error: state.reservation.error,
});

export default connect(mapStateToProps, actions)(Summary);
